import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircle, Home, RefreshCw } from 'lucide-react';
import InactivityTimer from '../components/InactivityTimer';

const DepositPaymentFailure = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const errorCode = searchParams.get('error');
    const errorDescription = searchParams.get('description');

    const handleRetry = () => {
        navigate('/online-deposit');
    };

    const handleGoHome = () => {
        navigate('/');
    };

    const getErrorMessage = () => {
        if (errorDescription) {
            return errorDescription;
        }

        switch (errorCode) {
            case 'PAYMENT_CANCELLED':
            case 'payment_cancelled':
                return 'Payment was cancelled by user';
            case 'PAYMENT_FAILED':
            case 'payment_failed':
                return 'Payment failed due to technical error';
            case 'INVALID_SIGNATURE':
            case 'signature_mismatch':
                return 'Payment verification failed';
            case 'PAYMENT_NOT_FOUND':
            case 'payment_not_found':
                return 'Payment record not found. Please try again.';
            case 'VERIFICATION_FAILED':
            case 'verification_failed':
                return 'We could not verify your payment right now.';
            case 'AMOUNT_MISMATCH':
            case 'amount_mismatch':
                return 'Payment amount mismatch detected.';
            case 'COLLECTION_NOT_FOUND':
            case 'collection_not_found':
                return 'Collection record not found for this payment.';
            case 'INSUFFICIENT_FUNDS':
                return 'Insufficient funds in your account';
            case 'CARD_DECLINED':
                return 'Your card was declined';
            case 'NETWORK_ERROR':
                return 'Network error occurred during payment';
            default:
                return 'Payment could not be processed. Please try again.';
        }
    };

    return (
        <>
            <InactivityTimer />
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
                    {/* Failure Icon */}
                    <div className="flex justify-center mb-6">
                        <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center animate-pulse">
                            <XCircle className="w-16 h-16 text-red-600" />
                        </div>
                    </div>

                    {/* Failure Message */}
                    <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">
                        Payment Failed
                    </h1>
                    <p className="text-center text-gray-600 mb-6">
                        Your deposit payment could not be processed
                    </p>

                    {/* Error Details */}
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <p className="text-red-800 text-center font-medium">
                            {getErrorMessage()}
                        </p>
                        {errorCode && (
                            <p className="text-red-600 text-center text-sm mt-2">
                                Error Code: {errorCode}
                            </p>
                        )}
                    </div>

                    {/* Suggestions */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <h3 className="font-semibold text-gray-800 mb-2">What you can do:</h3>
                        <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                            <li>Check your internet connection</li>
                            <li>Ensure sufficient balance in your account</li>
                            <li>Try a different payment method</li>
                            <li>Contact your bank if the issue persists</li>
                        </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-4">
                        <button
                            onClick={handleRetry}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition duration-200 shadow-lg"
                        >
                            <RefreshCw className="w-5 h-5" />
                            Try Again
                        </button>
                        <button
                            onClick={handleGoHome}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition duration-200"
                        >
                            <Home className="w-5 h-5" />
                            Go to Home
                        </button>
                    </div>

                    {/* Support Info */}
                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800 text-center">
                            <strong>Need Help?</strong> Please contact the reception desk for assistance with your payment.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DepositPaymentFailure;
